import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { tokens } from '../theme';

/**
 * Compact time stepper — two type-to-edit fields (Hr / Min) with no spinner
 * arrows. API is unchanged: initialHours/initialMinutes in, onChangeHours /
 * onChangeMinutes / onChangeTime (total minutes) out.
 */
const fieldSx = (disabled) => ({
  width: "3.25rem",
  textAlign: "center",
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: tokens.text,
  padding: "0.5rem 0.25rem",
  borderRadius: "8px",
  border: `1px solid ${tokens.border}`,
  background: disabled ? tokens.surfaceSubtle : tokens.card,
  outline: "none",
  appearance: "textfield",
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
  "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` },
  "&:hover": disabled ? {} : { borderColor: tokens.textMuted },
});

const unitSx = { fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted };

export default function MultiSectionClock({
  showHours = true, showMinutes = true, initialHours = 0, initialMinutes = 0,
  onChangeHours, onChangeMinutes, onChangeTime, disabled,
}) {
  const [hours, setHours] = useState(initialHours || 0);
  const [minutes, setMinutes] = useState(initialMinutes || 0);

  useEffect(() => { setHours(initialHours || 0); }, [initialHours]);
  useEffect(() => { setMinutes(initialMinutes || 0); }, [initialMinutes]);

  const commit = (h, m) => {
    const hh = Number.isNaN(parseInt(h, 10)) ? 0 : parseInt(h, 10);
    const mm = Number.isNaN(parseInt(m, 10)) ? 0 : parseInt(m, 10);
    onChangeTime?.(hh * 60 + mm);
  };

  const handleHoursChange = (e) => {
    const value = e.target.value;
    if (value === "") { setHours(""); return; }
    const intValue = parseInt(value, 10);
    if (isNaN(intValue)) return;
    const newHours = Math.min(Math.max(intValue, 0), 23);
    setHours(newHours);
    onChangeHours?.(newHours);
    commit(newHours, minutes);
  };

  const handleMinutesChange = (e) => {
    const value = e.target.value;
    if (value === "") { setMinutes(""); return; }
    const intValue = parseInt(value, 10);
    if (isNaN(intValue)) return;
    const newMinutes = Math.min(Math.max(intValue, 0), 59);
    setMinutes(newMinutes);
    onChangeMinutes?.(newMinutes);
    commit(hours, newMinutes);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {showHours && (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box component="input" type="number" min={0} max={23} value={hours}
            onChange={handleHoursChange} disabled={disabled} aria-label="hours" sx={fieldSx(disabled)} />
          <Box sx={unitSx}>Hr</Box>
        </Stack>
      )}
      {showMinutes && (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box component="input" type="number" min={0} max={59} value={minutes}
            onChange={handleMinutesChange} disabled={disabled} aria-label="minutes" sx={fieldSx(disabled)} />
          <Box sx={unitSx}>Min</Box>
        </Stack>
      )}
    </Stack>
  );
}
